import './styles.scss'

import React, { useEffect, useRef, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import ReactDOM from 'react-dom'

import smoothScroll from 'smoothscroll'
import { useActions, useValues } from 'kea'

import I18n from 'lib/i18n'
import Searchbox from '~/scenes/shared/searchbox'

import logic from './logic'

const setPhoto = (photo, width, height) => {
  if (!photo) {
    return null
  }
  const dimensions = `auto=format&ch=Width,DPR&fit=crop&w=${width}&h=${height}&dpr=${window.devicePixelRatio || 1}`
  return `${photo}${photo.indexOf('?') >= 0 ? '&' : '?'}${dimensions}`
}

const IMAGE_CHANGE_DELAY = 10000
const FIRST_IMAGE_CHANGE_DELAY = 5000
const DEBOUNCE_WAIT = 300

const PhotoArea = () => {
  const { showHow } = useActions(logic)
  const { categorySuggestions, coverPhoto, coverPhotoData, covers } = useValues(logic)

  const [activeIndex, setActiveIndex] = useState(covers.indexOf(coverPhoto))
  const [isLoaded, setIsLoaded] = useState(false)
  const [isFirstCover, setIsFirstCover] = useState(true)
  const [cover1, setCover1] = useState('')
  const [cover2, setCover2] = useState('')

  const photoAreaRef = useRef(null)
  const timeOutRef = useRef(null)
  const activeIndexRef = useRef(activeIndex)
  const isFirstCoverRef = useRef(isFirstCover)

  useEffect(() => {
    activeIndexRef.current = activeIndex
    isFirstCoverRef.current = isFirstCover
  }, [activeIndex, isFirstCover])

  const handleHowClick = event => {
    event.preventDefault()
    showHow()
    smoothScroll(document.querySelector('body'))
  }

  const setTimeOut = index => {
    if (timeOutRef.current) {
      window.clearTimeout(timeOutRef.current)
    }
    const nextIndex = (index + 1) % covers.length
    timeOutRef.current = setTimeout(
      () => {
        // eslint-disable-next-line no-use-before-define, @typescript-eslint/no-use-before-define
        updatePhoto(nextIndex)
      },
      activeIndexRef.current === index ? FIRST_IMAGE_CHANGE_DELAY : IMAGE_CHANGE_DELAY
    )
  }

  const updatePhoto = index => {
    const { clientWidth, clientHeight } = photoAreaRef.current
    const photoUrl = setPhoto(covers[index], clientWidth, clientHeight)

    ReactDOM.unstable_batchedUpdates(() => {
      if (activeIndexRef.current === index) {
        setIsLoaded(true)
        setCover1(photoUrl)
      } else {
        isFirstCoverRef.current ? setCover2(photoUrl) : setCover1(photoUrl)
        setActiveIndex(index)
        setIsFirstCover(prevState => !prevState)
      }
    })

    setTimeOut(index)
  }

  const handleResize = useDebouncedCallback(() => setTimeOut(activeIndexRef.current), DEBOUNCE_WAIT)

  useEffect(() => {
    updatePhoto(activeIndexRef.current)
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      window.clearTimeout(timeOutRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div id='homepage-photo-area' ref={photoAreaRef}>
      <div className='photo-area-photo' key='blur-bg' style={{ backgroundImage: `url('${coverPhotoData}')` }} />
      <div
        className={`photo-area-photo smooth-load${isLoaded ? ' smooth-loaded' : ''}`}
        key='big-bg-1'
        style={cover1 ? { backgroundImage: `url('${cover1}')` } : {}}
      />
      <div
        className={`photo-area-photo smooth-load-slow${isFirstCover ? '' : ' smooth-loaded'}`}
        key='big-bg-2'
        style={cover2 ? { backgroundImage: `url('${cover2}')` } : {}}
      />
      <div className='photo-area-photo dark-bg' />
      <div className='photo-area-content'>
        <h1>{I18n.t('react.homepage.photo.title')}</h1>
        <h2>{I18n.t('react.homepage.photo.subtitle')}</h2>
        <div className='form-block'>
          <div className='form-elements'>
            <Searchbox
              dataAttr='search-box-home-page'
              id='homepage'
              className='home-page-search-box'
              categorySuggestions={categorySuggestions}
              withButton
              withMagnifierIcon
            />
          </div>
        </div>
        <a className='how-link' href='/how' onClick={handleHowClick}>
          {I18n.t('react.homepage.photo.how_link')}
        </a>
      </div>
    </div>
  )
}

export default PhotoArea
